import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box, Typography, Paper, TextField, IconButton, Button,
    Avatar, Divider, CircularProgress, Snackbar, Alert,
    Chip, Stack, Grow
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DescriptionIcon from '@mui/icons-material/Description';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import axios from 'axios';
import { marked } from 'marked';
import API_URL from '../../config';

// Configure marked to convert single line breaks into <br> so plain-text
// AI responses (not just full markdown) still render with correct spacing.
marked.setOptions({
    breaks: true,
    gfm: true,
});

function AIAuditReport() {
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    const fileInputRef = useRef(null);
    const chatEndRef = useRef(null);

    // Generate a stable session id per page load (used for vector store + chat + report)
    const [sessionId] = useState(() => `audit_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);

    const [selectedFile, setSelectedFile] = useState(null);      // { name, size, file }
    const [uploading, setUploading] = useState(false);
    const [vectorStoreReady, setVectorStoreReady] = useState(false);

    const [messages, setMessages] = useState([
        { sender: 'bot', text: 'Hello! Upload a Word document to begin, then ask me anything about it. You can also generate an audit report.' }
    ]);
    const [chatInput, setChatInput] = useState('');
    const [chatLoading, setChatLoading] = useState(false);

    const [reportGenerating, setReportGenerating] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    // Log an action to the Activity Log (fire-and-forget, best effort).
    const logActivity = (action, entityId, details) => {
        try {
            axios.post(`${API_URL}/logs`, {
                module: 'Geo Audit',
                action,
                entity_id: entityId != null ? String(entityId) : null,
                details,
                source: 'Web'
            }, { headers });
        } catch (_) { /* ignore logging errors */ }
    };

    useEffect(() => {
        if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }, [messages, chatLoading]);

    const showSnack = (message, severity = 'info') => setSnackbar({ open: true, message, severity });
    const closeSnack = () => setSnackbar((s) => ({ ...s, open: false }));

    const isWordFile = (file) => {
        const ext = (file.name || '').split('.').pop().toLowerCase();
        return ['doc', 'docx'].includes(ext);
    };

    const handleFileChange = (e) => {
        const file = e.target.files && e.target.files[0];
        handleFile(file);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleFile = (file) => {
        if (!file) return;
        if (!isWordFile(file)) {
            showSnack('Only Word documents (.doc / .docx) are supported.', 'error');
            return;
        }
        setSelectedFile({ name: file.name, size: file.size, file });
        setVectorStoreReady(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files && e.dataTransfer.files[0];
        handleFile(file);
    };

    const handleDragOver = (e) => e.preventDefault();

    // Upload the Word file -> /generate_vector_store
    const handleUpload = async () => {
        if (!selectedFile || uploading) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('session_id', sessionId);
            formData.append('file', selectedFile.file);

            const res = await axios.post(`${API_URL}/ai-audit/generate_vector_store`, formData, {
                headers,
                timeout: 600000
            });

            const data = res.data || {};
            if (String(data.status).toLowerCase() === 'success') {
                setVectorStoreReady(true);
                showSnack(data.message || 'Vector store created successfully.', 'success');
                setMessages((prev) => [...prev, { sender: 'bot', text: data.message || 'Your document has been loaded. You can now start chatting.' }]);
                logActivity('UPLOAD', sessionId, `Uploaded document "${selectedFile.name}" (${(selectedFile.size / 1024).toFixed(1)} KB)`);
            } else {
                showSnack(data.message || 'Vector store creation failed.', 'error');
            }
        } catch (err) {
            console.error('Upload error:', err);
            const msg = err.response?.data?.message || err.message || 'Upload failed.';
            showSnack(msg, 'error');
        } finally {
            setUploading(false);
        }
    };

    // Send a chat message -> /chat
    const handleSendMessage = async () => {
        const text = chatInput.trim();
        if (!text || chatLoading) return;
        setChatInput('');
        setMessages((prev) => [...prev, { sender: 'user', text }]);
        setChatLoading(true);
        setMessages((prev) => [...prev, { sender: 'bot', text: '', isLoader: true }]);
        try {
            const res = await axios.post(
                `${API_URL}/ai-audit/chat`,
                { question: text, session_id: sessionId },
                { headers: { ...headers, 'Content-Type': 'application/json' }, timeout: 600000 }
            );
            const data = res.data || {};
            const reply = data.message || data.error || 'No response.';
            setMessages((prev) => {
                const base = [...prev];
                base[base.length - 1] = { sender: 'bot', text: reply };
                return base;
            });
            logActivity('CHAT', sessionId, `Asked: "${text}"`);
        } catch (err) {
            console.error('Chat error:', err);
            const msg = err.response?.data?.message || err.message || 'Sorry, there was an error.';
            setMessages((prev) => {
                const base = [...prev];
                base[base.length - 1] = { sender: 'bot', text: msg };
                return base;
            });
        } finally {
            setChatLoading(false);
        }
    };

    const clearFile = () => {
        setSelectedFile(null);
        setVectorStoreReady(false);
    };

    // Generate & download the audit report -> /generate_audit_report (PDF)
    const handleGenerateReport = async () => {
        if (reportGenerating) return;
        setReportGenerating(true);
        try {
            const res = await axios.post(
                `${API_URL}/ai-audit/generate_audit_report`,
                { session_id: sessionId },
                { headers: { ...headers, 'Content-Type': 'application/json' }, responseType: 'blob', timeout: 600000 }
            );

            const blob = res.data;
            const disposition = res.headers['content-disposition'] || '';
            let filename = 'audit_report.pdf';
            const match = disposition.match(/filename="?([^";]+)"?/i);
            if (match && match[1]) filename = match[1];
            if (!filename.toLowerCase().endsWith('.pdf')) filename += '.pdf';

            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 5000);

            showSnack('Audit report generated successfully.', 'success');
            logActivity('GENERATE', sessionId, `Generated audit report "${filename}"`);
        } catch (err) {
            console.error('Report generation error:', err);
            let msg = err.message || 'Report generation failed.';
            if (err.response?.data instanceof Blob) {
                try {
                    const text = await err.response.data.text();
                    const parsed = JSON.parse(text);
                    msg = parsed.message || parsed.error || msg;
                } catch (_) { /* ignore parse errors */ }
            } else if (err.response?.data?.message) {
                msg = err.response.data.message;
            }
            showSnack(msg, 'error');
        } finally {
            setReportGenerating(false);
        }
    };

    const renderMessageText = useCallback((text) => {
        if (!text) return '';
        try {
            return marked.parse(text);
        } catch (_) {
            // Replace newlines with <br> so plain text still renders cleanly
            return String(text).replace(/\n/g, '<br/>');
        }
    }, []);

    const formatSize = (bytes) => {
        if (!bytes) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    return (
        <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', height: { xs: 'calc(100vh - 2rem)', md: 'calc(100vh - 4rem)' }, display: 'flex', flexDirection: 'column', overflowY: 'hidden' }}>
            <Grow in={true} timeout={500}>
                <Box sx={{
                    mb: 2, mt: 2, p: 3, borderRadius: 3, flexShrink: 0,
                    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                    color: 'white', boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
                    position: 'relative', overflow: 'hidden',
                    display: 'flex', flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2
                }}>
                    <Box sx={{ position: 'relative', zIndex: 2 }}>
                        <Typography variant="h5" fontWeight="800" sx={{ mb: 0.5, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
                            <AutoAwesomeIcon sx={{ fontSize: 30, color: '#90caf9' }} />
                            Geo Audit
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                            Upload a Word document, chat with it, and generate an audit report
                        </Typography>
                    </Box>
                </Box>
            </Grow>

            {/* Upload card */}
            <Paper
                elevation={1}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                sx={{ mb: 1.5, p: 1.5, borderRadius: 2, border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', flexShrink: 0 }}
            >
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" spacing={2}>
                    <Button
                        variant="outlined"
                        component="label"
                        startIcon={<UploadFileIcon />}
                        sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#0b3d6e', color: '#0b3d6e', '&:hover': { borderColor: '#155b9e', bgcolor: 'rgba(11,61,110,0.04)' } }}
                    >
                        Choose Word File
                        <input type="file" hidden accept=".doc,.docx" onChange={handleFileChange} ref={fileInputRef} />
                    </Button>

                    {selectedFile ? (
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexGrow: 1 }}>
                            <Chip
                                icon={<DescriptionIcon />}
                                label={`${selectedFile.name} (${formatSize(selectedFile.size)})`}
                                onDelete={clearFile}
                                color={vectorStoreReady ? 'success' : 'primary'}
                                variant="outlined"
                            />
                            {vectorStoreReady && <Chip icon={<SmartToyIcon />} label="Ready to chat" color="success" size="small" />}
                        </Stack>
                    ) : (
                        <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                            or drag &amp; drop a .docx file here
                        </Typography>
                    )}

                    <Button
                        variant="contained"
                        onClick={handleUpload}
                        disabled={!selectedFile || uploading || vectorStoreReady}
                        startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
                        sx={{
                            textTransform: 'none', fontWeight: 600, borderRadius: 1.5,
                            bgcolor: '#0b3d6e', '&:hover': { bgcolor: '#082d52' },
                            '&:disabled': { bgcolor: '#b0c4d8', color: '#fff' },
                        }}
                    >
                        {uploading ? 'Uploading...' : vectorStoreReady ? 'Uploaded' : 'Upload & Index'}
                    </Button>
                </Stack>
            </Paper>

            {/* Chat card */}
            <Paper
                elevation={1}
                sx={{ display: 'flex', flexDirection: 'column', borderRadius: 2, border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', flexGrow: 1, minHeight: 0, mb: 1.5 }}
            >
                <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, bgcolor: '#fafafa', minHeight: 0 }}>
                    {messages.map((m, i) => (
                        <Box
                            key={i}
                            sx={{
                                display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2,
                                flexDirection: m.sender === 'user' ? 'row-reverse' : 'row',
                            }}
                        >
                            <Avatar sx={{ bgcolor: m.sender === 'user' ? '#0b3d6e' : '#155b9e', width: 32, height: 32 }}>
                                {m.sender === 'user' ? <PersonIcon sx={{ fontSize: 18 }} /> : <SmartToyIcon sx={{ fontSize: 18 }} />}
                            </Avatar>
                            <Box
                                sx={{
                                    maxWidth: '75%', px: 2, py: 1, borderRadius: 2,
                                    bgcolor: m.sender === 'user' ? '#0b3d6e' : '#ffffff',
                                    color: m.sender === 'user' ? '#ffffff' : '#1f2937',
                                    border: m.sender === 'user' ? 'none' : '1px solid #e5e7eb',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                }}
                            >
                                {m.isLoader ? (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <CircularProgress size={14} sx={{ color: m.sender === 'user' ? '#ffffff' : '#0b3d6e' }} />
                                        <Typography variant="body2">Thinking...</Typography>
                                    </Stack>
                                ) : m.sender === 'user' ? (
                                    <Typography variant="body2" sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                                        {m.text}
                                    </Typography>
                                ) : (
                                    <Typography
                                        variant="body2"
                                        component="div"
                                        sx={{
                                            lineHeight: 1.6,
                                            '& p': { m: 0, mb: 1, '&:last-of-type': { mb: 0 } },
                                            '& strong, & b': { fontWeight: 800, color: '#0f172a' },
                                            '& em': { fontStyle: 'italic' },
                                            '& h1, & h2, & h3, & h4': { fontSize: '0.95rem', fontWeight: 800, m: 0, mt: 1.5, mb: 0.75, color: '#0f172a' },
                                            '& ul, & ol': { m: 0, pl: 2.5, mb: 1 },
                                            '& li': { mb: 0.5 },
                                            '& pre': { bgcolor: '#f1f5f9', p: 1, borderRadius: 1, overflowX: 'auto', my: 1, fontSize: '0.75rem' },
                                            '& code': { fontFamily: 'monospace', bgcolor: '#f1f5f9', px: 0.5, borderRadius: 0.5, fontSize: '0.78rem' },
                                            '& a': { color: '#0b3d6e', textDecoration: 'underline' },
                                            '& hr': { border: 'none', borderTop: '1px solid #e2e8f0', my: 1 },
                                            '& table': { borderCollapse: 'collapse', width: '100%', my: 1, fontSize: '0.75rem' },
                                            '& th, & td': { border: '1px solid #e2e8f0', px: 0.75, py: 0.5, textAlign: 'left' },
                                            '& th': { bgcolor: '#f8fafc', fontWeight: 700 },
                                            '& blockquote': { m: 0, my: 1, pl: 1.5, borderLeft: '3px solid #cbd5e1', color: '#475569' },
                                        }}
                                        dangerouslySetInnerHTML={{ __html: renderMessageText(m.text) }}
                                    />
                                )}
                            </Box>
                        </Box>
                    ))}
                    <div ref={chatEndRef} />
                </Box>

                <Divider />

                {/* Chat input */}
                <Box sx={{ p: 2, bgcolor: '#ffffff', display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        placeholder="Type your question about the document..."
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                        disabled={!vectorStoreReady}
                    />
                    <IconButton
                        onClick={handleSendMessage}
                        disabled={!chatInput.trim() || chatLoading || !vectorStoreReady}
                        sx={{ bgcolor: '#0b3d6e', color: '#fff', '&:hover': { bgcolor: '#082d52' }, '&:disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' } }}
                    >
                        <SendIcon />
                    </IconButton>
                </Box>
            </Paper>

            {/* Action buttons */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ flexShrink: 0, pb: 1 }}>
                <Button
                    variant="contained"
                    onClick={handleGenerateReport}
                    disabled={reportGenerating}
                    startIcon={reportGenerating ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfIcon />}
                    sx={{
                        textTransform: 'none', fontWeight: 600, borderRadius: 1.5,
                        bgcolor: '#d1b156', '&:hover': { bgcolor: '#bda04a' },
                        '&:disabled': { bgcolor: '#e5d4a8', color: '#fff' },
                    }}
                >
                    {reportGenerating ? 'Generating Report...' : 'Generate Audit Report'}
                </Button>

                <Button
                    variant="outlined"
                    onClick={() => {
                        setMessages([{ sender: 'bot', text: 'Hello! Upload a Word document to begin, then ask me anything about it.' }]);
                        setSelectedFile(null);
                        setVectorStoreReady(false);
                    }}
                    startIcon={<DeleteIcon />}
                    sx={{ textTransform: 'none', borderRadius: 1.5, color: '#0b3d6e', borderColor: '#e5e7eb', '&:hover': { borderColor: '#0b3d6e', bgcolor: 'rgba(11,61,110,0.04)' } }}
                >
                    Reset Chat
                </Button>
            </Stack>

            {/* Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={closeSnack} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={closeSnack} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default AIAuditReport;

